import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import SharedModal from '../../../shared/_TBComponents/Modal';
import Button from '../../../shared/_TBComponents/Button';
import DatePicker from '../../../shared/_TBComponents/DatePicker';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import { SquareCheckbox, Dash } from '../components/presentational/tables/MaxesGoalsTable';
import { getMaxEntryFieldConfig, withUnit } from '../utils/maxesGoalsHelpers';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-height: calc(100vh - 150px);
  font-family: "Nunito Sans";
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  cursor: pointer;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  flex-shrink: 0;
`;

const TableScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const AthleteTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #FFFFFF;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9E9E9E;
    padding: 0 8px 8px 8px;
  }

  td {
    padding: 8px;
    border-top: 1px solid #F5F5F5;
    font-size: 14px;
    color: #1A1A1A;
  }
`;

const NumberInput = styled.input`
  width: 110px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #444444;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;

  &:focus {
    outline: none;
    border-color: #1A1A1A;
  }
`;

const RepsCell = styled.div`
  position: relative;
  display: inline-flex;
`;

const RepsInput = styled.input`
  width: 76px;
  height: 36px;
  padding: 0 26px 0 10px;
  border: 1px solid #444444;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: #1A1A1A;
  }
`;

const RepsArrows = styled.div`
  position: absolute;
  top: 2px;
  right: 10px;
  bottom: 2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RepsArrowButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  color: #9E9E9E;

  &:hover {
    color: #1A1A1A;
  }
`;

const RepsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  width: 100%;
`;

const RepsHeaderArrows = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoButton = styled.button`
  display: flex;
  border: none;
  background: none;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  color: #9E9E9E;

  &:hover {
    color: #1A1A1A;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid #EEEEEE;
  flex-shrink: 0;
`;

const NestedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  gap: 10px;
`;

const NestedModalFooter = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const getColumns = (fieldConfig) => {
  switch (fieldConfig.mode) {
    case 'weightReps':
      return [{ key: 'primary1', label: 'Weight' }, { key: 'reps', label: 'Reps', stepper: true }];
    case 'weightOnly':
      return [{ key: 'primary1', label: 'Weight' }];
    case 'repsOnly':
      return [{ key: 'reps', label: 'Reps', stepper: true }];
    case 'timed':
      return [{ key: 'primary1', label: 'Minutes' }, { key: 'primary2', label: 'Seconds' }];
    case 'feetInches':
      return [{ key: 'primary1', label: 'Feet' }, { key: 'primary2', label: 'Inches' }];
    case 'inchesOnly':
      return [{ key: 'primary1', label: 'Inches' }];
    default:
      return [{ key: 'primary1', label: fieldConfig.unitLabel || 'Value' }];
  }
};

const getTrackingDescription = (fieldConfig) => {
  switch (fieldConfig.mode) {
    case 'weightReps': return 'weight and reps';
    case 'weightOnly': return 'weight only';
    case 'repsOnly': return 'reps only';
    case 'timed': return 'time (minutes and seconds)';
    case 'feetInches': return 'distance (feet and inches)';
    case 'inchesOnly': return 'distance (inches)';
    default: return (fieldConfig.unitLabel || 'a custom value').toLowerCase();
  }
};

const EXERCISES_URL = `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/exercises`;

const formatCurrentMax = (current) => (
  current?.value ? withUnit(current.value, current.unitAbbr) : null
);

// Rows start empty so an untouched row is skipped
const buildInitialEntries = (athletes, fieldConfig) => athletes.reduce((acc, athlete) => {
  acc[athlete.userId] = {
    primary1: '',
    primary2: '',
    reps: fieldConfig.mode === 'weightReps' ? (fieldConfig.defaultReps || '1') : '',
  };
  return acc;
}, {});

const AddMaxesModal = ({
  isOpen, title, athletes, exercise, currentMaxByUserId, isSubmitting, allowLock, onSubmit, onClose,
}) => {
  const fieldConfig = getMaxEntryFieldConfig(exercise);
  const columns = getColumns(fieldConfig);
  // The field that shows a row has been filled in
  const gateKey = fieldConfig.mode === 'repsOnly' ? 'reps' : 'primary1';

  const [dateSet, setDateSet] = useState(new Date());
  const [setCurrentMax, setSetCurrentMax] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [entriesByUserId, setEntriesByUserId] = useState({});
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [isTrackingInfoOpen, setIsTrackingInfoOpen] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDateSet(new Date());
      setSetCurrentMax(false);
      setIsLocked(false);
      setEntriesByUserId(buildInitialEntries(athletes, fieldConfig));
      setIsDiscardConfirmOpen(false);
      setIsTrackingInfoOpen(false);
      // Wait for the popup to take focus first
      const focusTimer = setTimeout(() => firstInputRef.current?.focus(), 0);
      return () => clearTimeout(focusTimer);
    }
    return undefined;
  }, [isOpen]);

  const updateEntry = (userId, field, value) => {
    setEntriesByUserId({
      ...entriesByUserId,
      [userId]: { ...entriesByUserId[userId], [field]: value },
    });
  };

  // Reps can't go below 1
  const bumpReps = (userId, delta) => {
    setEntriesByUserId((prev) => {
      const current = Number(prev[userId]?.reps) || 0;
      return { ...prev, [userId]: { ...prev[userId], reps: String(Math.max(1, current + delta)) } };
    });
  };

  const bumpAllReps = (delta) => {
    setEntriesByUserId((prev) => {
      const next = { ...prev };
      athletes.forEach((athlete) => {
        const current = Number(next[athlete.userId]?.reps) || 0;
        const reps = String(Math.max(1, current + delta));
        next[athlete.userId] = { ...next[athlete.userId], reps };
      });
      return next;
    });
  };

  // Blank rows are skipped
  const enteredEntries = athletes
    .map((athlete) => ({ userId: athlete.userId, ...entriesByUserId[athlete.userId] }))
    .filter((entry) => entry[gateKey] !== '' && entry[gateKey] !== undefined);

  const handleSubmit = () => {
    if (enteredEntries.length === 0) return;
    onSubmit({
      dateSet, setCurrentMax, isLocked, entries: enteredEntries,
    });
  };

  // Warn before closing if anything has been entered
  const handleCloseAttempt = () => {
    if (enteredEntries.length > 0) {
      setIsDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <SharedModal
        isOpen={isOpen}
        onRequestClose={handleCloseAttempt}
        customButton
        hideButton
        customMaxWidth='560px'
        customBorderRadius='8px'
      >
        <ModalContent>
          <Text
            styledWrapperProps={{ flexShrink: 0 }}
            styledTextProps={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A' }}
          >
            {title}
          </Text>
          <FieldRow>
            <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#1A1A1A' }}>
              Date of Max Update
            </Text>
            <DatePicker singleDate startDate={dateSet} setStartDate={setDateSet} textColor='#1A1A1A' />
          </FieldRow>
          <CheckboxGroup>
            {fieldConfig.mode !== 'health' && (
              <CheckboxRow onClick={() => setSetCurrentMax(!setCurrentMax)}>
                <SquareCheckbox
                  checked={setCurrentMax}
                  onChange={(e) => setSetCurrentMax(e.target.checked)}
                />
                <Text styledTextProps={{ fontSize: '14px', color: '#1A1A1A' }}>
                  Set entries as current max
                </Text>
              </CheckboxRow>
            )}
            {allowLock && (
              <CheckboxRow onClick={() => setIsLocked(!isLocked)}>
                <SquareCheckbox
                  checked={isLocked}
                  onChange={(e) => setIsLocked(e.target.checked)}
                />
                <Text styledTextProps={{ fontSize: '14px', color: '#1A1A1A' }}>
                  Lock entries
                </Text>
                <Text styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}>
                  (only coach can update)
                </Text>
              </CheckboxRow>
            )}
          </CheckboxGroup>
          <TableScrollArea>
            <AthleteTable>
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>Current Max</th>
                  {columns.map((col, colIndex) => (
                    <th key={col.key}>
                      {col.stepper ? (
                        <RepsHeader>
                          {col.label}
                          {colIndex === 0 && (
                            <InfoButton
                              type='button'
                              aria-label="About this exercise's tracking type"
                              onClick={() => setIsTrackingInfoOpen(true)}
                            >
                              <Icon icon='info2' size={14} />
                            </InfoButton>
                          )}
                          <RepsHeaderArrows>
                            <RepsArrowButton
                              type='button'
                              tabIndex={-1}
                              aria-label='Increase all reps'
                              onClick={() => bumpAllReps(1)}
                            >
                              <Icon icon='up-arrow' size={10} />
                            </RepsArrowButton>
                            <RepsArrowButton
                              type='button'
                              tabIndex={-1}
                              aria-label='Decrease all reps'
                              onClick={() => bumpAllReps(-1)}
                            >
                              <Icon icon='down-arrow' size={10} />
                            </RepsArrowButton>
                          </RepsHeaderArrows>
                        </RepsHeader>
                      ) : (
                        <HeaderLabelRow>
                          {col.label}
                          {colIndex === 0 && (
                            <InfoButton
                              type='button'
                              aria-label="About this exercise's tracking type"
                              onClick={() => setIsTrackingInfoOpen(true)}
                            >
                              <Icon icon='info2' size={14} />
                            </InfoButton>
                          )}
                        </HeaderLabelRow>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {athletes.map((athlete, athleteIndex) => (
                  <tr key={athlete.userId}>
                    <td>{`${athlete.first} ${athlete.last}`}</td>
                    <td>
                      {formatCurrentMax(currentMaxByUserId[athlete.userId]) || <Dash />}
                    </td>
                    {columns.map((col, colIndex) => (
                      <td key={col.key}>
                        {col.stepper ? (
                          <RepsCell>
                            <RepsInput
                              ref={athleteIndex === 0 && colIndex === 0 ? firstInputRef : undefined}
                              type='text'
                              inputMode='numeric'
                              value={entriesByUserId[athlete.userId]?.reps ?? ''}
                              onChange={(e) => updateEntry(athlete.userId, 'reps', e.target.value)}
                            />
                            <RepsArrows>
                              <RepsArrowButton
                                type='button'
                                tabIndex={-1}
                                aria-label='Increase reps'
                                onClick={() => bumpReps(athlete.userId, 1)}
                              >
                                <Icon icon='up-arrow' size={10} />
                              </RepsArrowButton>
                              <RepsArrowButton
                                type='button'
                                tabIndex={-1}
                                aria-label='Decrease reps'
                                onClick={() => bumpReps(athlete.userId, -1)}
                              >
                                <Icon icon='down-arrow' size={10} />
                              </RepsArrowButton>
                            </RepsArrows>
                          </RepsCell>
                        ) : (
                          <NumberInput
                            ref={athleteIndex === 0 && colIndex === 0 ? firstInputRef : undefined}
                            type='number'
                            value={entriesByUserId[athlete.userId]?.[col.key] ?? ''}
                            onChange={(e) => updateEntry(athlete.userId, col.key, e.target.value)}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </AthleteTable>
          </TableScrollArea>
          <ButtonGroup>
            <Button
              type='button'
              name='Cancel'
              fontSize='14px'
              txtColor='#444444'
              bgColor='#FFF'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={handleCloseAttempt}
            />
            <Button
              type='button'
              name={isSubmitting ? 'Submitting...' : 'Submit'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#444444'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={isSubmitting || enteredEntries.length === 0}
              onClick={handleSubmit}
            />
          </ButtonGroup>
        </ModalContent>
      </SharedModal>

      <SharedModal
        isOpen={isDiscardConfirmOpen}
        onRequestClose={() => setIsDiscardConfirmOpen(false)}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
        overlayBackgroundColor='rgba(0, 0, 0, 0.75)'
      >
        <NestedModalContent>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Unsaved Changes
          </Text>
          <Text styledWrapperProps={{ marginBottom: '16px' }}>
            You have unsaved changes. Are you sure you want to leave?
          </Text>
          <NestedModalFooter>
            <Button
              type='button'
              name='Cancel'
              fontSize='14px'
              txtColor='#757575'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={() => setIsDiscardConfirmOpen(false)}
            />
            <Button
              type='button'
              name='Leave'
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#FF0505'
              borderColor='#FF0505'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={() => { setIsDiscardConfirmOpen(false); onClose(); }}
            />
          </NestedModalFooter>
        </NestedModalContent>
      </SharedModal>

      <SharedModal
        isOpen={isTrackingInfoOpen}
        onRequestClose={() => setIsTrackingInfoOpen(false)}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
        overlayBackgroundColor='rgba(0, 0, 0, 0.75)'
      >
        <NestedModalContent>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            About This Exercise&rsquo;s Tracking
          </Text>
          <Text styledWrapperProps={{ marginBottom: '16px' }}>
            {`"${exercise?.name}" is currently set to track `}
            {getTrackingDescription(fieldConfig)}
            . If that is not right, you can change how it is tracked from Exercise Management.
          </Text>
          <NestedModalFooter>
            <Button
              type='button'
              name='Got it'
              fontSize='14px'
              txtColor='#757575'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={() => setIsTrackingInfoOpen(false)}
            />
            <Button
              name='Go to Exercise Management'
              href={EXERCISES_URL}
              target='_blank'
              rel='noopener noreferrer'
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#444444'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
            />
          </NestedModalFooter>
        </NestedModalContent>
      </SharedModal>
    </>
  );
};

AddMaxesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  athletes: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
  })),
  exercise: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    track: PropTypes.number,
    inputType: PropTypes.string,
    unit: PropTypes.string,
    unitAbbr: PropTypes.string,
    unitName: PropTypes.string,
  }),
  currentMaxByUserId: PropTypes.objectOf(PropTypes.shape({
    value: PropTypes.string,
    unitAbbr: PropTypes.string,
  })),
  isSubmitting: PropTypes.bool,
  allowLock: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

AddMaxesModal.defaultProps = {
  title: '',
  athletes: [],
  exercise: null,
  currentMaxByUserId: {},
  isSubmitting: false,
  allowLock: true,
};

export default AddMaxesModal;
